import {
  ChecklistGroup,
  Comment,
  NeedCtx,
  Role,
  RoleDef,
  Row,
  StatusDef,
  StatusKey,
} from "./types";

/**
 * Status table — mirrors the STATUS object in the HTML mockup (lines 461–480).
 * `need(ctx)` receives live checklist/comment counts so the "back with writer" copy stays accurate.
 */
export const STATUS: Record<StatusKey, StatusDef> = {
  notstarted: {
    label: "Not started",
    stage: 0,
    tone: "grey",
    icon: "circleDashed",
    holder: "writer",
    need: () => "begin writing",
  },
  writing: {
    label: "In progress",
    stage: 0,
    tone: "blue",
    icon: "pencil",
    holder: "writer",
    need: () => "complete and submit for review",
  },

  with_writer: {
    label: "In progress",
    icon: "clock",
    tone: "blue",
    stage: 0,
    back: false,
    holder: "writer",
    need: (c: NeedCtx) => {
      if (c.role === "reviewer") {
        return `address ${c.pendingCount} checklist item${c.pendingCount !== 1 ? "s" : ""
          } flagged in review`;
      }

      if (c.role === "verifier") {
        return `address ${c.verifyCommentCount} comment${c.verifyCommentCount !== 1 ? "s" : ""
          } from verification`;
      }

      if (c.role === "approver") {
        return `address ${c.approvalCommentCount} comment${c.approvalCommentCount !== 1 ? "s" : ""
          } from approval`;
      }

      return "complete and submit for review";
    },
  },
  with_reviewer: {
    label: "Under review",
    icon: "clock",
    tone: "blue",
    stage: 1,
    back: false,
    holder: "reviewer",
    need: () => "complete the review checklist",
  },
  review: {
    label: "Under review",
    stage: 1,
    tone: "blue",
    icon: "checklist",
    holder: "reviewer",
    need: () => "complete the review checklist",
  },
  corr_rev: {
    label: "Revisions requested — review",
    stage: 1,
    tone: "amber",
    back: true,
    icon: "back",
    holder: "writer",
    need: (c: NeedCtx) =>
      `address ${c.pendingCount} checklist item${c.pendingCount !== 1 ? "s" : ""} flagged in review`,
  },
  verify: {
    label: "Under verification",
    stage: 2,
    tone: "blue",
    icon: "verify",
    holder: "verifier",
    need: () => "verify the data and submit comments",
  },
  with_verifier: {
    label: "Under verification",
    icon: "clock",
    tone: "blue",
    stage: 2,
    holder: "verifier",
    back: false,
    need: () => "verify the data and submit comments",
  },
  corr_ver: {
    label: "Revisions requested — verification",
    stage: 2,
    tone: "amber",
    back: true,
    icon: "back",
    holder: "writer",
    need: (c: NeedCtx) =>
      `address ${c.verifyCommentCount} comment${c.verifyCommentCount !== 1 ? "s" : ""} from verification`,
  },
  approval: {
    label: "Pending approval",
    stage: 3,
    tone: "blue",
    icon: "stamp",
    holder: "approver",
    need: () => "approve, or return with comments",
  },
  with_approver: {
    label: "Pending approval",
    icon: "clock",
    tone: "blue",
    stage: 3,
    holder: "approver",
    back: false,
    need: () => "approve, or return with comments",
  },
  corr_app: {
    label: "Revisions requested — approval",
    stage: 3,
    tone: "amber",
    back: true,
    icon: "back",
    holder: "writer",
    need: (c: NeedCtx) =>
      `address ${c.approvalCommentCount} comment${c.approvalCommentCount !== 1 ? "s" : ""} from approval`,
  },
  approved: {
    label: "Approved",
    stage: 3,
    tone: "green",
    icon: "circleCheck",
    holder: null,
    need: () => "Final PDF generated — no further action",
  },
};

export const ROLES: Record<Role, RoleDef> = {
  writer: {
    name: "Writer",
    icon: "pencil",
    sub: "Reports you are writing",
    note: "The writer writes, submits, and works through feedback.",
  },

  reviewer: {
    name: "Reviewer",
    icon: "checklist",
    sub: "Reports waiting on your review",
    note: "The reviewer works through the checklist.",
  },

  verifier: {
    name: "Verifier",
    icon: "verify",
    sub: "Reports waiting on verification",
    note: "The verifier reviews the report and adds comments.",
  },

  approver: {
    name: "Approver",
    icon: "stamp",
    sub: "Reports waiting on approval",
    note: "The approver reviews and approves the report.",
  },
};

export const ANCHORS = ["Title page", "Section 2.3", "Appendix C", "Table 1.2"];

// export const getWriterAgenda = async (): Promise<{ data: Row[] }> => {
//   return {
//     data: [
//       {
//         id: 23,
//         proj: "10313",
//         cust: "Hanwha QCells",
//         bom: "BOM 1 — CO-1",
//         type: "Module Intake",
//         edit: 1,
//         st: "writing",
//         start: "2026-08-11",
//         due: "20-09-2026",
//         rel: "in 12 days",
//         writer: "charan.gurram",
//         owner: "charan.gurram",
//         is_new_report: true,
//         report_version: null,
//       },
//       {
//         id: 23,
//         proj: "10320",
//         cust: "Astronergy",
//         bom: "BOM 1",
//         type: "Module PQP Interim 1",
//         edit: 2,
//         st: "corr_rev",
//         start: "2026-08-04",
//         due: "26-09-2026",
//         rel: "in 5 wks",
//         writer: "arun.kumar",
//         owner: "arun.kumar",
//         backFrom: "Review",
//         backBy: "jennifer.bravo",
//         backAt: "yesterday 16:30",
//         liveCk: true,
//         is_new_report: false,
//         report_version: 2,
//       },
//       {
//         id: 23,
//         proj: "10401",
//         cust: "JinkoSolar",
//         bom: "BOM 2",
//         type: "Module PQP Final",
//         edit: 1,
//         st: "notstarted",
//         start: null,
//         due: "30-09-2026",
//         rel: "in 25 days",
//         writer: "priya.sharma",
//         owner: "priya.sharma",
//         is_new_report: true,
//         report_version: null,
//       },
//     ],
//   };
// };

// export const getReviewerAgenda = async (): Promise<{ data: Row[] }> => {
//   return {
//     data: [
//       {
//         id: 4,
//         proj: "10287",
//         cust: "Trina Solar",
//         bom: "BOM 2",
//         type: "Module PQP Interim 1",
//         edit: 1,
//         st: "review",
//         start: "2026-07-28",
//         due: "02-09-2026",
//         rel: "in 12 days",
//         writer: "saikiran.miriyala",
//         owner: "jennifer.bravo",
//         liveCk: true,
//         lastSaved: "yesterday 16:42",
//          is_new_report: false,
//       },
//       {
//         id: 5,
//         proj: "10422",
//         cust: "LONGi",
//         bom: "BOM 1",
//         type: "Module Intake",
//         edit: 2,
//         st: "review",
//         start: "2026-08-10",
//         due: "15-09-2026",
//         rel: "in 10 days",
//         writer: "arun.kumar",
//         owner: "michael.smith",
//         liveCk: true,
//          is_new_report: false,
//       },
//       {
//         id: 6,
//         proj: "10501",
//         cust: "JA Solar",
//         bom: "BOM 3",
//         type: "Module PQP Final",
//         edit: 1,
//         st: "review",
//         start: "2026-08-15",
//         due: "20-09-2026",
//         rel: "in 15 days",
//         writer: "priya.sharma",
//         owner: "david.johnson",
//         liveCk: true,
//          is_new_report: false,
//       },
//     ],
//   };
// };

// export const getVerifierAgenda = async (): Promise<{ data: Row[] }> => {
//   return {
//     data: [
//       {
//         id: 7,
//         proj: "10355",
//         cust: "Canadian Solar",
//         bom: "BOM 2",
//         type: "Module PQP Interim 2",
//         edit: 1,
//         st: "verify",
//         start: "2026-08-12",
//         due: "25-09-2026",
//         rel: "in 20 days",
//         writer: "rahul.kumar",
//         owner: "edgar.ibarra",
//          is_new_report: false,
//       },
//       {
//         id: 8,
//         proj: "10401",
//         cust: "JinkoSolar",
//         bom: "BOM 3",
//         type: "Module PQP Final",
//         edit: 2,
//         st: "verify",
//         start: "2026-08-08",
//         due: "28-09-2026",
//         rel: "in 23 days",
//         writer: "meena.r",
//         owner: "edgar.ibarra",
//          is_new_report: false,
//       },
//       {
//         id: 9,
//         proj: "10477",
//         cust: "Trina Solar",
//         bom: "BOM 1",
//         type: "Module Intake",
//         edit: 1,
//         st: "verify",
//         start: "2026-08-18",
//         due: "30-09-2026",
//         rel: "in 25 days",
//         writer: "arun.kumar",
//         owner: "edgar.ibarra",
//          is_new_report: false,
//       },
//     ],
//   };
// };

// export const getApproverAgenda = async (): Promise<{ data: Row[] }> => {
//   return {
//     data: [
//       {
//         id: 10,
//         proj: "10256",
//         cust: "Canadian Solar",
//         bom: "BOM 1",
//         type: "Module PQP Interim 2",
//         edit: 1,
//         st: "approval",
//         start: "2026-05-30",
//         due: "19-09-2026",
//         rel: "2 days late",
//         late: true,
//         writer: "saikiran.miriyala",
//         owner: "jeff.cleland",
//          is_new_report: false,

//       },
//       {
//         id: 11,
//         proj: "10190",
//         cust: "VSUN",
//         bom: "BOM 4",
//         type: "Module PQP Final",
//         edit: 2,
//         st: "approval",
//         start: "2026-05-12",
//         due: "22-09-2026",
//         rel: "tomorrow",
//         writer: "arun.kumar",
//         owner: "jeff.cleland",
//          is_new_report: false,
//       },
//       {
//         id: 12,
//         proj: "10510",
//         cust: "LONGi",
//         bom: "BOM 2",
//         type: "Module Intake",
//         edit: 1,
//         st: "approval",
//         start: "2026-08-05",
//         due: "29-09-2026",
//         rel: "in 24 days",
//         writer: "rahul.kumar",
//         owner: "jeff.cleland",
//          is_new_report: false,
//       },
//     ],
//   };
// };

// export const getAgendaByRole = async (role: Role): Promise<{ data: Row[] }> => {
//   switch (role) {
//     case "writer":
//       return getWriterAgenda();

//     case "reviewer":
//       return getReviewerAgenda();

//     case "verifier":
//       return getVerifierAgenda();

//     case "approver":
//       return getApproverAgenda();

//     default:
//       return { data: [] };
//   }
// };
